import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { ReactComponent as Logomark } from 'assets/images/logos/Logomark.svg';
import { useTranslation } from 'react-i18next';
import { useTheme } from 'contexts/ThemeContext';
import { RootState } from 'config/redux/store';
import { getUserRole } from 'utils/auth/auth';

interface WelcomeProps {
  setIsLoggedIn: (isLoggedIn: boolean) => void;
}

const Welcome: React.FC<WelcomeProps> = ({ setIsLoggedIn }) => {
  const { t } = useTranslation();
  const { isDark } = useTheme();
  const navigate = useNavigate();
  const isExpired = useSelector((state: RootState) => state.user.isExpired);

  // If the user has no active subscription (expired), push them straight to the
  // plan picker. Admin/SuperAdmin bypass the gate so they can access the platform.
  useEffect(() => {
    const role = (getUserRole() || '').toLowerCase().replace(/\s+/g, '');
    const isAdmin = role === 'admin' || role === 'superadmin';
    if (isExpired && !isAdmin) {
      setIsLoggedIn(true);
      navigate('/onboarding', { replace: true });
    }
  }, [isExpired, navigate, setIsLoggedIn]);

  const handleContinue = () => {
    setIsLoggedIn(true);
    navigate('/dashboard');
  };

  return (
    <div
      className={`min-h-screen flex items-center justify-center p-4 font-geist transition-colors duration-300 ${
        isDark ? 'bg-fc-dark' : 'bg-fc-light'
      }`}
    >
      <div className="w-full max-w-[480px] flex flex-col items-center gap-6">
        {/* Logo */}
        <Logomark className="w-20 h-20" />

        {/* Title + Description */}
        <div className="w-full text-center space-y-4">
          <h1
            className={`text-2xl font-bold leading-8 ${
              isDark ? 'text-fc-dark-text-primary' : 'text-fc-text-primary'
            }`}
          >
            {t('welcome.title')}
          </h1>
          <p
            className={`text-base font-normal leading-6 ${
              isDark ? 'text-fc-dark-text-primary' : 'text-fc-text-primary'
            }`}
          >
            {t('welcome.description')}
          </p>
        </div>

        {/* Continue Button */}
        <button
          type="button"
          onClick={handleContinue}
          className="w-full h-12 bg-fc-gradient-btn text-white text-base font-medium leading-6 rounded-fc-lg hover:opacity-90 transition-opacity flex items-center justify-center"
        >
          {t('welcome.continueButton')}
        </button>
      </div>
    </div>
  );
};

export default Welcome;
